import {RefObject, useCallback, useEffect, useState, useRef} from 'react';
import {WebcamRefTypes} from '@chekinapp/ui';
import {type Keypoint} from '@tensorflow-models/face-landmarks-detection';
import {throttle, useSyncRef} from '@chekin/core';
import {usePreloadFaceModels} from './usePreloadFaceModels';
import type {FacePosition, OnFaceDetectionSignal} from '../types';

const LEFT_THRESHOLD = 1.2;
const RIGHT_THRESHOLD = -1.2;
const UP_THRESHOLD = 0.3;
const EAR_THRESHOLD = 0.18;

const FACE_POSITION_THROTTLE = 150;
const BLINK_CHECK_THROTTLE = 75;
const DETECTION_FRAME_SKIP = 1;

const calculateEAR = (landmarks: Keypoint[], eyeIndices: number[]) => {
  const [p1, p2, p3, p4] = eyeIndices.map(idx => landmarks[idx]);
  const vertical = Math.abs(p2.y - p3.y);
  const horizontal = Math.abs(p1.x - p4.x);
  return vertical / horizontal;
};

interface UseClientFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  shouldPreload?: boolean;
  onSignal?: OnFaceDetectionSignal;
}

export const useClientFaceDetection = ({
  videoRef,
  shouldPreload = true,
  onSignal,
}: UseClientFaceDetectionProps) => {
  const [facePosition, setFacePosition] = useState<FacePosition>('unknown');
  const detectionFrameId = useRef<number | null>(null);
  const frameSkipCounter = useRef<number>(0);
  const blinkStartTime = useRef<number | null>(null);
  const lastBlinkTime = useRef<number>(0);
  const pendingBlink = useRef(false);
  const onSignalRef = useSyncRef(onSignal);

  const {
    preload,
    isLoading: isModelLoading,
    detector,
  } = usePreloadFaceModels({shouldPreload});

  const detectorRef = useSyncRef(detector);

  const throttledSetFacePosition = useRef(
    throttle((position: FacePosition) => {
      setFacePosition(position);
    }, FACE_POSITION_THROTTLE),
  ).current;

  const throttledProcessBlink = useRef(
    throttle((leftEAR: number, rightEAR: number, isCenter: boolean, now: number) => {
      const areEyesClosed =
        isCenter && leftEAR < EAR_THRESHOLD && rightEAR < EAR_THRESHOLD;

      if (areEyesClosed) {
        if (!blinkStartTime.current) {
          blinkStartTime.current = now;
        }
      } else if (blinkStartTime.current) {
        const blinkDuration = now - blinkStartTime.current;
        if (
          blinkDuration >= 80 &&
          blinkDuration <= 400 &&
          now - lastBlinkTime.current > 1000
        ) {
          pendingBlink.current = true;
          lastBlinkTime.current = now;
        }
        blinkStartTime.current = null;
      }
    }, BLINK_CHECK_THROTTLE),
  ).current;

  useEffect(() => {
    if (!shouldPreload) {
      return;
    }

    async function initializeDetection() {
      if (detector) {
        return;
      }

      await preload();
    }

    void initializeDetection();
  }, [detector, preload, shouldPreload]);

  const detectFace = useCallback(async () => {
    const videoElement = videoRef?.current?.video;
    const detector = detectorRef.current;

    if (!detector || !videoElement || videoElement.readyState !== 4) {
      detectionFrameId.current = requestAnimationFrame(detectFace);
      return;
    }

    frameSkipCounter.current =
      (frameSkipCounter.current + 1) % (DETECTION_FRAME_SKIP + 1);
    if (frameSkipCounter.current !== 0) {
      detectionFrameId.current = requestAnimationFrame(detectFace);
      return;
    }

    try {
      const faces = await detector.estimateFaces(videoElement);

      if (faces.length > 0) {
        const face = faces[0];
        const keypoints = face.keypoints;

        if (keypoints.length < 468) {
          detectionFrameId.current = requestAnimationFrame(detectFace);
          return;
        }

        const now = Date.now();

        const leftEyeInner = keypoints[133];
        const rightEyeInner = keypoints[362];
        const noseTip = keypoints[1];

        const eyeCenterX = (leftEyeInner.x + rightEyeInner.x) / 2;
        const eyeCenterY = (leftEyeInner.y + rightEyeInner.y) / 2;
        const eyeDistance = Math.sqrt(
          Math.pow(rightEyeInner.x - leftEyeInner.x, 2) +
            Math.pow(rightEyeInner.y - leftEyeInner.y, 2),
        );

        const offsetX = (noseTip.x - eyeCenterX) / eyeDistance;
        const offsetY = (noseTip.y - eyeCenterY) / eyeDistance;

        const isCenter = offsetX > -0.2 && offsetX < 0.5 && offsetY > 0.8;

        let position: FacePosition;
        if (offsetY < UP_THRESHOLD) {
          position = 'up';
        } else if (offsetX > LEFT_THRESHOLD) {
          position = 'left';
        } else if (offsetX < RIGHT_THRESHOLD) {
          position = 'right';
        } else if (isCenter) {
          position = 'center';
        } else {
          position = 'unknown';
        }
        throttledSetFacePosition(position);

        const leftEyeIndices = [33, 159, 145, 133];
        const rightEyeIndices = [362, 386, 374, 263];

        const leftEAR = calculateEAR(keypoints, leftEyeIndices);
        const rightEAR = calculateEAR(keypoints, rightEyeIndices);

        throttledProcessBlink(leftEAR, rightEAR, isCenter, now);

        const didBlink = pendingBlink.current;
        pendingBlink.current = false;

        onSignalRef.current?.({
          source: 'client',
          facePosition: position,
          offsetX,
          offsetY,
          didBlink,
        });
      } else {
        throttledSetFacePosition('unknown');
        onSignalRef.current?.({source: 'client', facePosition: 'unknown'});
      }
    } catch (error) {
      console.error('Face error detection', error);
    }

    detectionFrameId.current = requestAnimationFrame(detectFace);
  }, [
    detectorRef,
    videoRef,
    throttledProcessBlink,
    throttledSetFacePosition,
    onSignalRef,
  ]);

  const startDetection = useCallback(() => {
    if (!shouldPreload) {
      return;
    }

    if (detectionFrameId.current) {
      cancelAnimationFrame(detectionFrameId.current);
    }
    detectionFrameId.current = requestAnimationFrame(detectFace);
  }, [detectFace, shouldPreload]);

  useEffect(() => {
    return () => {
      if (detectionFrameId.current) {
        cancelAnimationFrame(detectionFrameId.current);
        detectionFrameId.current = null;
      }
    };
  }, []);

  return {
    isModelLoading,
    facePosition,
    startDetection,
  };
};
